import React, { useState, useEffect, useMemo } from 'react';
import { Calculator, FlaskConical, ArrowRight, Check, RotateCcw, Target, TrendingUp } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';

// Parse the lei_formula to create calculable functions
const createCalculator = (measure) => {
  if (!measure?.lei_formula) return null;
  
  const formula = measure.lei_formula.formula;
  const variables = measure.lei_formula.variables || [];
  
  // Map common formula patterns to calculation functions
  const calculators = {
    // Training Scope Index: (Participation Rate + Reach) / 2
    'training_scope': (values) => {
      const participationRate = (values.participants / values.eligiblePopulation) * 100 || 0;
      const reach = (values.participants / values.totalWorkforce) * 100 || 0;
      return {
        result: (participationRate + reach) / 2,
        breakdown: { 'Participation Rate': participationRate, 'Reach': reach }
      };
    },
    // SME Training Scope Index: (SME Participation Rate + SME Reach) / 2
    'sme_training': (values) => {
      const smeParticipation = (values.smeTrained / values.totalSME) * 100 || 0;
      const smeReach = (values.uniqueSMETrained / values.totalSME) * 100 || 0;
      return {
        result: (smeParticipation + smeReach) / 2,
        breakdown: { 'SME Participation Rate': smeParticipation, 'SME Reach': smeReach }
      };
    },
    // Community Learning Index: (Forum Participation + Engagement + Quality) / 3
    'community_learning': (values) => {
      const forumParticipation = (values.activeForumParticipants / values.eligiblePopulation) * 100 || 0;
      const engagementRaw = (values.totalPosts + values.totalComments + values.totalLikes) / values.eligiblePopulation;
      const engagementNormalized = Math.min(engagementRaw * 10, 100); // Normalize to 0-100
      const qualityScore = ((values.totalLikes) / (values.totalPosts + values.totalComments + 1)) * 100 || 0;
      return {
        result: (forumParticipation + engagementNormalized + qualityScore) / 3,
        breakdown: { 'Forum Participation': forumParticipation, 'Engagement Index': engagementNormalized, 'Quality Score': qualityScore }
      };
    },
    // Voluntary Learning Engagement: (Completions / Workforce) * 100
    'voluntary_engagement': (values) => {
      const result = (values.voluntaryCompletions / values.totalWorkforce) * 100 || 0;
      return { result, breakdown: { 'Engagement Rate': result } };
    },
    // Learning Tech Utilization: (Active Users / Licensed Users) * 100
    'tech_utilization': (values) => {
      const result = (values.activeUsers / values.licensedUsers) * 100 || 0;
      return { result, breakdown: { 'Utilization Rate': result } };
    },
    // Learning Culture Index: (Engagement + Manager + Peer + Leadership) / 4
    'learning_culture': (values) => {
      const avg = (values.voluntaryEngagement + values.managerSupport + values.peerSupport + values.leadershipAdvocacy) / 4;
      return {
        result: avg,
        breakdown: {
          'Voluntary Engagement': values.voluntaryEngagement,
          'Manager Support': values.managerSupport,
          'Peer Support': values.peerSupport,
          'Leadership Advocacy': values.leadershipAdvocacy
        }
      };
    },
    // Training Performance Index: 0.4*Assessment + 0.3*Pass + 0.3*Mastery
    'training_performance': (values) => {
      const result = (0.4 * values.assessmentAvg) + (0.3 * values.passRate) + (0.3 * values.masteryIndex);
      return {
        result,
        breakdown: {
          'Assessment Avg (40%)': values.assessmentAvg * 0.4,
          'Pass Rate (30%)': values.passRate * 0.3,
          'Mastery Index (30%)': values.masteryIndex * 0.3
        }
      };
    },
    // NPS: (Raw + 100) / 2
    'nps': (values) => {
      const promoterPct = (values.promoters / values.totalResponses) * 100 || 0;
      const detractorPct = (values.detractors / values.totalResponses) * 100 || 0;
      const rawNPS = promoterPct - detractorPct;
      const normalizedNPS = (rawNPS + 100) / 2;
      return {
        result: normalizedNPS,
        breakdown: { 'Promoters %': promoterPct, 'Detractors %': detractorPct, 'Raw NPS': rawNPS }
      };
    },
    // SAPI: 0.3*Pre + 0.4*Post + 0.3*Uplift
    'skills_assessment': (values) => {
      const result = (0.3 * values.preScore) + (0.4 * values.postScore) + (0.3 * values.upliftScore);
      return {
        result,
        breakdown: {
          'Pre-Learning (30%)': values.preScore * 0.3,
          'Post-Learning (40%)': values.postScore * 0.4,
          'Skill Uplift (30%)': values.upliftScore * 0.3
        }
      };
    },
    // Skills Coverage: (Gap Closure + Bench Depth) / 2
    'skills_coverage': (values) => {
      const gapClosure = (values.currentSkillLevel / values.targetSkillLevel) * 100 || 0;
      const benchDepth = (values.readySuccessors / values.criticalPositions) * 100 || 0;
      return {
        result: (gapClosure + benchDepth) / 2,
        breakdown: { 'Skills Gap Closure': gapClosure, 'Bench Depth': benchDepth }
      };
    },
    // Certification Depth: 0.6*Current + 0.2*Advanced + 0.2*Renewal
    'certification_depth': (values) => {
      const currentRate = (values.currentCerts / values.eligibleEmployees) * 100 || 0;
      const advancedRate = (values.advancedCerts / values.eligibleEmployees) * 100 || 0;
      const renewalRate = (values.renewedCerts / values.expiringCerts) * 100 || 0;
      const result = (0.6 * currentRate) + (0.2 * advancedRate) + (0.2 * renewalRate);
      return {
        result,
        breakdown: {
          'Current Certs (60%)': currentRate * 0.6,
          'Advanced (20%)': advancedRate * 0.2,
          'Renewals (20%)': renewalRate * 0.2
        }
      };
    },
    // Readiness Index: (Target Time / Actual Time) * 100
    'readiness_index': (values) => {
      const result = (values.targetDays / values.actualDays) * 100 || 0;
      return { result: Math.min(result, 150), breakdown: { 'Competency Acceleration': result } };
    },
    // Cost Efficiency: (Target Cost / Actual Cost) * 100
    'cost_efficiency': (values) => {
      const result = (values.targetCost / values.actualCost) * 100 || 0;
      return { result: Math.min(result, 150), breakdown: { 'Cost Efficiency': result } };
    },
    // Scheduling Velocity: (Target Delivery Cost / Actual Delivery Cost) * 100
    'scheduling_velocity': (values) => {
      const result = (values.targetDeliveryCost / values.actualDeliveryCost) * 100 || 0;
      return { result: Math.min(result, 150), breakdown: { 'Delivery Efficiency': result } };
    },
    // KPI Alignment: Composite measure
    'kpi_alignment': (values) => {
      // Simplified: average of alignment scores
      const result = (values.strategicAlignment + values.impactAttribution + values.correlationScore) / 3;
      return {
        result,
        breakdown: {
          'Strategic Alignment': values.strategicAlignment,
          'Impact Attribution': values.impactAttribution,
          'Correlation Score': values.correlationScore
        }
      };
    },
  };
  
  return calculators[measure.id] || _genericCalculator(measure);
};

// Generic composite calculator for measures without a bespoke function:
// averages the entered variable values (each treated as an indexed 0–100 input).
const _genericCalculator = (measure) => {
  const vars = measure?.lei_formula?.variables || [];
  if (!vars.length) return null;
  return (values) => {
    const nums = vars.map((_, i) => Number(values[`v${i}`]) || 0);
    const provided = nums.filter((n, i) => values[`v${i}`] !== undefined && values[`v${i}`] !== '');
    const result = provided.length ? provided.reduce((a, b) => a + b, 0) / provided.length : 0;
    const breakdown = {};
    vars.forEach((v, i) => { breakdown[v.name] = nums[i]; });
    return { result, breakdown };
  };
};

// Input field configurations for each measure
const getInputFields = (measureId, measure) => {
  const fieldConfigs = {
    'training_scope': [
      { key: 'participants', label: 'No. of Participants', placeholder: 'e.g., 800' },
      { key: 'eligiblePopulation', label: 'Eligible Population', placeholder: 'e.g., 1000' },
      { key: 'totalWorkforce', label: 'Total Workforce', placeholder: 'e.g., 2000' },
    ],
    'sme_training': [
      { key: 'smeTrained', label: 'No. of SMEs Trained', placeholder: 'e.g., 50' },
      { key: 'uniqueSMETrained', label: 'Unique SMEs Trained', placeholder: 'e.g., 45' },
      { key: 'totalSME', label: 'Total SMEs', placeholder: 'e.g., 100' },
    ],
    'community_learning': [
      { key: 'activeForumParticipants', label: 'Active Forum Participants', placeholder: 'e.g., 250' },
      { key: 'eligiblePopulation', label: 'Eligible Population', placeholder: 'e.g., 1000' },
      { key: 'totalPosts', label: 'Total Posts', placeholder: 'e.g., 500' },
      { key: 'totalComments', label: 'Total Comments', placeholder: 'e.g., 1500' },
      { key: 'totalLikes', label: 'Total Likes/Upvotes', placeholder: 'e.g., 3000' },
    ],
    'voluntary_engagement': [
      { key: 'voluntaryCompletions', label: 'Voluntary Course Completions', placeholder: 'e.g., 500' },
      { key: 'totalWorkforce', label: 'Total Workforce', placeholder: 'e.g., 2000' },
    ],
    'tech_utilization': [
      { key: 'activeUsers', label: 'Active Monthly LMS Users', placeholder: 'e.g., 1500' },
      { key: 'licensedUsers', label: 'Licensed Users', placeholder: 'e.g., 2000' },
    ],
    'learning_culture': [
      { key: 'voluntaryEngagement', label: 'Voluntary Engagement Score (0-100)', placeholder: 'e.g., 30' },
      { key: 'managerSupport', label: 'Manager Support Score (0-100)', placeholder: 'e.g., 75' },
      { key: 'peerSupport', label: 'Peer Support Score (0-100)', placeholder: 'e.g., 60' },
      { key: 'leadershipAdvocacy', label: 'Leadership Advocacy Score (0-100)', placeholder: 'e.g., 80' },
    ],
    'training_performance': [
      { key: 'assessmentAvg', label: 'Assessment Score Average (0-100)', placeholder: 'e.g., 85' },
      { key: 'passRate', label: 'Pass Rate (0-100)', placeholder: 'e.g., 90' },
      { key: 'masteryIndex', label: 'Mastery Index (0-100)', placeholder: 'e.g., 70' },
    ],
    'nps': [
      { key: 'promoters', label: 'No. of Promoters (9-10 rating)', placeholder: 'e.g., 600' },
      { key: 'detractors', label: 'No. of Detractors (0-6 rating)', placeholder: 'e.g., 200' },
      { key: 'totalResponses', label: 'Total Survey Responses', placeholder: 'e.g., 1000' },
    ],
    'skills_assessment': [
      { key: 'preScore', label: 'Pre-Learning Skills Score (0-100)', placeholder: 'e.g., 50' },
      { key: 'postScore', label: 'Post-Learning Skills Score (0-100)', placeholder: 'e.g., 75' },
      { key: 'upliftScore', label: 'Skill Uplift Score (0-100)', placeholder: 'e.g., 80' },
    ],
    'skills_coverage': [
      { key: 'currentSkillLevel', label: 'Average Current Skill Level', placeholder: 'e.g., 75' },
      { key: 'targetSkillLevel', label: 'Target Skill Level', placeholder: 'e.g., 100' },
      { key: 'readySuccessors', label: 'Ready Successors', placeholder: 'e.g., 30' },
      { key: 'criticalPositions', label: 'Critical Positions', placeholder: 'e.g., 50' },
    ],
    'certification_depth': [
      { key: 'currentCerts', label: 'Employees with Current Certifications', placeholder: 'e.g., 800' },
      { key: 'advancedCerts', label: 'Employees with Advanced Certifications', placeholder: 'e.g., 300' },
      { key: 'renewedCerts', label: 'Certifications Renewed', placeholder: 'e.g., 180' },
      { key: 'expiringCerts', label: 'Certifications Due for Renewal', placeholder: 'e.g., 200' },
      { key: 'eligibleEmployees', label: 'Total Eligible Employees', placeholder: 'e.g., 1000' },
    ],
    'readiness_index': [
      { key: 'targetDays', label: 'Target Days to Competency', placeholder: 'e.g., 60' },
      { key: 'actualDays', label: 'Actual Average Days to Competency', placeholder: 'e.g., 75' },
    ],
    'cost_efficiency': [
      { key: 'targetCost', label: 'Target Cost per Participant ($)', placeholder: 'e.g., 500' },
      { key: 'actualCost', label: 'Actual Cost per Participant ($)', placeholder: 'e.g., 600' },
    ],
    'scheduling_velocity': [
      { key: 'targetDeliveryCost', label: 'Target Delivery Cost ($)', placeholder: 'e.g., 10000' },
      { key: 'actualDeliveryCost', label: 'Actual Delivery Cost ($)', placeholder: 'e.g., 12000' },
    ],
    'kpi_alignment': [
      { key: 'strategicAlignment', label: 'Strategic Alignment Score (0-100)', placeholder: 'e.g., 75' },
      { key: 'impactAttribution', label: 'Impact Attribution Score (0-100)', placeholder: 'e.g., 60' },
      { key: 'correlationScore', label: 'Correlation Score (0-100)', placeholder: 'e.g., 70' },
    ],
  };
  
  return fieldConfigs[measureId] || _genericFields(measure);
};

// Generic input fields derived from the formula's variables (0–100 each).
const _genericFields = (measure) => {
  const vars = measure?.lei_formula?.variables || [];
  return vars.map((v, i) => ({ key: `v${i}`, label: `${v.name} (0-100)`, placeholder: 'e.g., 70' }));
};

export const LEICalculatorDrawer = ({ 
  open, 
  onOpenChange, 
  measure, 
  currentImprovement = 0,
  liveCurrentScore = null,
  onApply 
}) => {
  const [inputValues, setInputValues] = useState({});
  const [targetValues, setTargetValues] = useState({});
  const [targetMode, setTargetMode] = useState('percentage'); // 'percentage' or 'values'
  const [targetPercentage, setTargetPercentage] = useState(currentImprovement || 2);

  const applyLiveBaseline = () => {
    if (liveCurrentScore == null) return;
    const seeded = {};
    (inputFields || []).forEach((f) => { seeded[f.key] = Math.round(liveCurrentScore * 10) / 10; });
    setInputValues(seeded);
  };
  
  const inputFields = useMemo(() => getInputFields(measure?.id, measure), [measure]);
  const calculator = useMemo(() => createCalculator(measure), [measure]);
  
  // Calculate current LEI from input values
  const currentResult = useMemo(() => {
    if (!calculator || Object.keys(inputValues).length === 0) return null;
    try {
      return calculator(inputValues);
    } catch (e) {
      return null;
    }
  }, [calculator, inputValues]);
  
  // Calculate target LEI from target values
  const targetResult = useMemo(() => {
    if (!calculator || targetMode !== 'values' || Object.keys(targetValues).length === 0) return null;
    try {
      return calculator(targetValues);
    } catch (e) {
      return null;
    }
  }, [calculator, targetMode, targetValues]);
  
  // Calculate improvement percentage
  const improvement = useMemo(() => {
    if (targetMode === 'percentage') {
      return targetPercentage;
    }
    if (currentResult && targetResult) {
      return targetResult.result - currentResult.result;
    }
    return 0;
  }, [targetMode, targetPercentage, currentResult, targetResult]);
  
  const targetLEI = useMemo(() => {
    if (targetMode === 'values' && targetResult) {
      return targetResult.result;
    }
    if (currentResult) {
      return currentResult.result + targetPercentage;
    }
    return null;
  }, [targetMode, targetResult, currentResult, targetPercentage]);
  
  // Reset form when measure changes
  useEffect(() => {
    setInputValues({});
    setTargetValues({});
    setTargetPercentage(currentImprovement || 2);
  }, [measure?.id, currentImprovement]);
  
  const handleInputChange = (key, value) => {
    setInputValues(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };
  
  const handleTargetValueChange = (key, value) => {
    setTargetValues(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };
  
  const handleReset = () => {
    setInputValues({});
    setTargetValues({});
    setTargetPercentage(currentImprovement || 2);
  };
  
  const handleApply = () => {
    if (onApply) {
      // Return just the improvement number for backwards compatibility
      onApply(improvement);
    }
    onOpenChange(false);
  };
  
  const copyCurrentToTarget = () => {
    setTargetValues({ ...inputValues });
  };
  
  if (!measure) return null;
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[500px] sm:max-w-[500px] overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FlaskConical className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <SheetTitle className="text-lg">LEI Calculator</SheetTitle>
              <SheetDescription className="text-sm">{measure.name}</SheetDescription>
            </div>
          </div>
        </SheetHeader>
        
        <div className="mt-6 space-y-6">
          {/* Formula Display */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <p className="text-xs font-medium text-blue-700 mb-1">Formula</p>
              <p className="font-mono text-sm text-blue-800">{measure.lei_formula?.formula}</p>
            </CardContent>
          </Card>
          
          {/* Current State Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-slate-600" />
              <h3 className="font-semibold text-slate-900">Current State</h3>
            </div>

            {liveCurrentScore != null && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2" data-testid="calculator-live-baseline">
                <span className="text-xs text-teal-800">
                  Live baseline from your imported data: <span className="font-bold">{Math.round(liveCurrentScore * 10) / 10}</span>
                </span>
                {inputFields.length > 0 && inputFields.every((f) => /^v\d+$/.test(f.key)) && (
                  <button
                    type="button"
                    onClick={applyLiveBaseline}
                    className="text-xs font-semibold text-teal-700 hover:text-teal-900 underline underline-offset-2"
                    data-testid="calculator-use-live-baseline"
                  >
                    Pre-fill current state
                  </button>
                )}
              </div>
            )}
            
            <div className="grid gap-3">
              {inputFields.map(field => (
                <div key={field.key} className="space-y-1">
                  <Label className="text-xs text-slate-600">{field.label}</Label>
                  <Input
                    type="number"
                    placeholder={field.placeholder}
                    value={inputValues[field.key] || ''}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className="h-9"
                    data-testid={`current-${field.key}`}
                  />
                </div>
              ))}
            </div>
            
            {/* Current LEI Result */}
            {currentResult && (
              <Card className="bg-slate-100 border-slate-200">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">Current LEI:</span>
                    <span className="text-2xl font-bold text-slate-900">
                      {currentResult.result.toFixed(1)}%
                    </span>
                  </div>
                  <div className="space-y-1 text-xs text-slate-600">
                    {Object.entries(currentResult.breakdown).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span>{key}:</span>
                        <span className="font-mono">{typeof value === 'number' ? value.toFixed(1) : value}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* Flow Arrow */}
          <div className="flex justify-center py-2">
            <ArrowRight className="h-6 w-6 text-slate-300" />
          </div>
          
          {/* Target State Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-teal-600" />
              <h3 className="font-semibold text-slate-900">Target State</h3>
            </div>
            
            <Tabs value={targetMode} onValueChange={setTargetMode} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="percentage">By Percentage</TabsTrigger>
                <TabsTrigger value="values">By Values</TabsTrigger>
              </TabsList>
              
              <TabsContent value="percentage" className="space-y-3 mt-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Target Improvement (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    step="0.5"
                    value={targetPercentage}
                    onChange={(e) => setTargetPercentage(parseFloat(e.target.value) || 0)}
                    className="h-9"
                    data-testid="target-percentage"
                  />
                </div>
                {currentResult && (
                  <p className="text-sm text-slate-600">
                    Target LEI: <span className="font-semibold text-teal-600">{(currentResult.result + targetPercentage).toFixed(1)}%</span>
                    <span className="text-slate-400"> (from {currentResult.result.toFixed(1)}%)</span>
                  </p>
                )}
              </TabsContent>
              
              <TabsContent value="values" className="space-y-3 mt-3">
                <div className="flex justify-end">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={copyCurrentToTarget}
                    className="text-xs"
                  >
                    Copy Current Values
                  </Button>
                </div>
                <div className="grid gap-3">
                  {inputFields.map(field => (
                    <div key={`target-${field.key}`} className="space-y-1">
                      <Label className="text-xs text-slate-600">Target {field.label}</Label>
                      <Input
                        type="number"
                        placeholder={field.placeholder}
                        value={targetValues[field.key] || ''}
                        onChange={(e) => handleTargetValueChange(field.key, e.target.value)}
                        className="h-9"
                        data-testid={`target-${field.key}`}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Target LEI Result */}
                {targetResult && (
                  <Card className="bg-teal-50 border-teal-200">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-teal-700">Target LEI:</span>
                        <span className="text-2xl font-bold text-teal-700">
                          {targetResult.result.toFixed(1)}%
                        </span>
                      </div>
                      <div className="space-y-1 text-xs text-teal-600">
                        {Object.entries(targetResult.breakdown).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span>{key}:</span>
                            <span className="font-mono">{typeof value === 'number' ? value.toFixed(1) : value}%</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Calculated Improvement */}
          {(currentResult || targetMode === 'percentage') && (
            <Card className="bg-gradient-to-r from-teal-500 to-teal-600 text-white">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-teal-100">Improvement to Apply</p>
                  <p className="text-4xl font-bold my-2">+{(improvement || 0).toFixed(1)}%</p>
                  {currentResult && targetLEI && (
                    <p className="text-sm text-teal-100">
                      {currentResult.result.toFixed(1)}% → {targetLEI.toFixed(1)}%
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        
        <SheetFooter className="mt-6 gap-2">
          <Button variant="outline" onClick={handleReset} className="flex-1">
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button 
            onClick={handleApply} 
            className="flex-1 bg-teal-600 hover:bg-teal-700"
            disabled={improvement <= 0}
            data-testid="apply-calculator-btn"
          >
            <Check className="h-4 w-4 mr-2" />
            Apply to Analysis
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default LEICalculatorDrawer;
